import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  DatePicker,
  Table,
  Spin,
  message,
  Typography,
  Select,
  Alert,
  Statistic,
  Divider,
  Tabs,
  Empty,
  Space,
  Grid,
  Modal,
  Switch,
  Progress
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  BankOutlined,
  BarChartOutlined,
  PrinterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  DashboardOutlined,
  WarningOutlined,
  TransactionOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { reportService } from '../../services/reportService';
import FinancialSummary from '../../components/FinancialSummary';
import useFinancialRefresh from '../../hooks/useFinancialRefresh';
import { generateSectionReportPDF, previewSectionReportPDF } from '../../services/reportPdfService';
import { useProductCategoriesOptions } from '../../hooks/useProductCategories'
import dayjs from 'dayjs';
import 'dayjs/locale/th';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

// ฟังก์ชันจัดรูปแบบตัวเลขที่ปลอดภัย (แก้ไขปัญหา NaN)
const safeFormatCurrency = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return 'ไม่มีข้อมูลหรือไม่มีสิทธิ์';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return '฿0';
  }

  return `฿${numValue.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeFormatNumber = (value, suffix = '') => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return `0${suffix ? ' ' + suffix : ''}`;
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return `0${suffix ? ' ' + suffix : ''}`;
  }

  return `${numValue.toLocaleString('th-TH')}${suffix ? ' ' + suffix : ''}`;
};

const ReportsPage = () => {
  const { categories, error, refresh } = useProductCategoriesOptions()
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const reportRequest = useRef(0);
  const [loadedDateRange, setLoadedDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState(''); // สำหรับติดตาม tab ที่เลือก
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [reportOptions, setReportOptions] = useState({
    groupBy: 'day',
    category: '',
    contactId: '',
    status: '',
    period: 'month',
    lowStock: false
  });

  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const reports = [
    {
      key: 'dashboard',
      title: 'แดชบอร์ด',
      icon: <DashboardOutlined />,
      description: 'ภาพรวมสถานะธุรกิจและตัวชี้วัดสำคัญ',
      color: '#722ed1'
    },
    {
      key: 'inventory',
      title: 'รายงานต้นทุนสินค้าคงเหลือ',
      icon: <ShoppingCartOutlined />,
      description: 'รายงานมูลค่าและต้นทุนสินค้าคงเหลือ',
      color: '#13c2c2'
    },
    {
      key: 'sales',
      title: 'รายงานยอดขาย',
      icon: <DollarOutlined />,
      description: 'รายงานยอดขายและกำไรตามช่วงเวลา',
      color: '#52c41a'
    },
    {
      key: 'expenses',
      title: 'รายงานค่าใช้จ่าย',
      icon: <FileTextOutlined />,
      description: 'รายงานค่าใช้จ่ายแยกตามหมวดหมู่',
      color: '#fa8c16'
    },
    {
      key: 'financial',
      title: 'รายงานงบการเงิน',
      icon: <BankOutlined />,
      description: 'รายงานสถานะการเงินโดยรวม',
      color: '#1890ff'
    },
    {
      key: 'profit-loss',
      title: 'รายงานกำไร/ขาดทุน',
      icon: <BarChartOutlined />,
      description: 'รายงานกำไรขาดทุนแยกตามช่วงเวลา',
      color: '#eb2f96'
    }
  ];

  const fetchReport = async () => {
    if (!selectedReport) return;

    const request = ++reportRequest.current;
    setLoading(true);
    setReportData(null);
    setFinancialData(null);
    try {
      let data;
      const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
      const endDate = dateRange?.[1]?.format('YYYY-MM-DD');
      if (selectedReport !== 'inventory' && (!startDate || !endDate)) throw new Error('กรุณาเลือกช่วงวันที่ให้ครบ');

      switch (selectedReport) {
        case 'dashboard':
          data = await reportService.getDashboardReport(startDate, endDate);
          console.log('dashboard', data);
          setActiveTab('overview'); // ตั้งค่า default tab
          break;
        case 'inventory':
          data = await reportService.getInventoryReport(
            reportOptions.category,
            reportOptions.lowStock
          );
          setActiveTab('summary'); // ตั้งค่า default tab
          break;
        case 'sales':
          data = await reportService.getSalesReport(
            startDate,
            endDate,
            reportOptions.contactId,
            reportOptions.groupBy
          );
          console.log('sales', data);
          // ตั้งค่า active tab ตาม groupBy
          setActiveTab(reportOptions.groupBy === 'day' ? 'daily' :
            reportOptions.groupBy === 'month' ? 'monthly' : 'yearly');
          break;
        case 'expenses':
          data = await reportService.getExpensesReport(
            startDate,
            endDate,
            reportOptions.category,
            reportOptions.status,
            reportOptions.groupBy
          );
          setActiveTab('summary'); // ตั้งค่า default tab
          break;
        case 'financial':
          data = await reportService.getFinancialReport(startDate, endDate);
          console.log('financial', data);
          setActiveTab('summary'); // ตั้งค่า default tab
          console.log('summary:', data)
          break;
        case 'profit-loss':
          data = await reportService.getProfitLossReport(
            startDate,
            endDate,
            reportOptions.period
          );

          console.log('profit-loss', data);

          // ตั้งค่า active tab ตาม period
          setActiveTab(reportOptions.period === 'day' ? 'daily' :
            reportOptions.period === 'week' ? 'weekly' :
              reportOptions.period === 'month' ? 'monthly' : 'yearly');
          break;
        default:
          throw new Error('Invalid report type');
      }

      let shared = null;
      if (selectedReport !== 'inventory') {
        shared = await reportService.getSummary(startDate, endDate, {
          ...(selectedReport === 'sales' ? { contact_id: reportOptions.contactId } : {}),
          ...(selectedReport === 'expenses' ? { category: reportOptions.category, status: reportOptions.status } : {})
        });
        if (selectedReport === 'sales') shared = { ...shared, ledger:null, reconciliation:null, documents:{receipts:shared.documents.receipts,expenses:null,difference:null} };
        if (selectedReport === 'expenses') shared = { ...shared, ledger:null, reconciliation:null, documents:{receipts:null,expenses:shared.documents.expenses,difference:null} };
        data = Array.isArray(data) ? Object.assign(data,{financial_summary:shared}) : {...data,financial_summary:shared};
      }
      if (request !== reportRequest.current) return;
      setLoadedDateRange(dateRange);
      setFinancialData(shared);
      setReportData(data);
      message.success('โหลดรายงานเรียบร้อยแล้ว');
    } catch (error) {
      if (request !== reportRequest.current) return;
      console.error('Error fetching report:', error);
      message.error('เกิดข้อผิดพลาดในการโหลดรายงาน: ' + error.message);
    } finally {
      if (request === reportRequest.current) setLoading(false);
    }
  };

  useFinancialRefresh(() => { if (selectedReport) fetchReport(); });

  useEffect(() => {
    reportRequest.current += 1;
    setReportData(null);
    setFinancialData(null);
    setLoadedDateRange(null);
    setLoading(false);
  }, [selectedReport, dateRange, reportOptions]);

  // ฟังก์ชันสำหรับดาวน์โหลด PDF ตาม section ที่เลือก
  const handleDownloadSectionPDF = async () => {
    if (!reportData || !selectedReport || !activeTab) {
      message.warning('กรุณาโหลดรายงานและเลือก tab ที่ต้องการพิมพ์ PDF');
      return;
    }

    setPdfLoading(true);
    try {
      const reportTitle = reports.find(r => r.key === selectedReport)?.title;
      const currentDateRange = ['inventory'].includes(selectedReport) ? null : loadedDateRange;

      await generateSectionReportPDF(selectedReport, activeTab, reportData, reportTitle, currentDateRange);
      message.success('ดาวน์โหลด PDF เรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error generating section PDF:', error);
      message.error('เกิดข้อผิดพลาดในการสร้าง PDF: ' + error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // ฟังก์ชันสำหรับพรีวิว PDF ตาม section ที่เลือก
  const handlePreviewSectionPDF = async () => {
    if (!reportData || !selectedReport || !activeTab) {
      message.warning('กรุณาโหลดรายงานและเลือก tab ที่ต้องการพรีวิว PDF');
      return;
    }

    setPdfLoading(true);
    try {
      const reportTitle = reports.find(r => r.key === selectedReport)?.title;
      const currentDateRange = ['inventory'].includes(selectedReport) ? null : loadedDateRange;

      await previewSectionReportPDF(selectedReport, activeTab, reportData, reportTitle, currentDateRange);
      message.success('เปิดพรีวิว PDF แล้ว');
    } catch (error) {
      console.error('Error previewing section PDF:', error);
      message.error('เกิดข้อผิดพลาดในการพรีวิว PDF: ' + error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // Component สำหรับปุ่ม PDF
  const PDFButtons = () => {
    if (!reportData || !activeTab) return null;

    const getSectionName = () => {
      const sectionNames = {
        // Dashboard
        'overview': 'ภาพรวม',
        'activities': 'กิจกรรมล่าสุด',
        'events': 'กำหนดการที่จะมาถึง',
        // Inventory
        'summary': 'สรุปภาพรวม',
        'products': 'รายละเอียดสินค้า',
        // Sales
        'daily': 'รายวัน',
        'monthly': 'รายเดือน',
        'yearly': 'รายปี',
        // Expenses
        'time': 'ตามช่วงเวลา',
        'category': 'ตามหมวดหมู่',
        'supplier': 'ตามผู้จำหน่าย',
        // Financial
        'cashflow': 'รับ–จ่ายตามเอกสาร',
        // Profit-Loss
        'weekly': 'รายสัปดาห์'
      };
      return sectionNames[activeTab] || activeTab;
    };

    return (
      <Space>
        <Button
          onClick={handlePreviewSectionPDF}
          loading={pdfLoading}
          icon={<EyeOutlined />}
          size="small"
        >
          พรีวิว PDF ({getSectionName()})
        </Button>
        <Button
          onClick={handleDownloadSectionPDF}
          loading={pdfLoading}
          icon={<DownloadOutlined />}
          size="small"
          type="dashed"
        >
          ดาวน์โหลด PDF ({getSectionName()})
        </Button>
      </Space>
    );
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    switch (selectedReport) {
      case 'dashboard':
        return <DashboardReportContent data={reportData} isMobile={isMobile} onTabChange={setActiveTab} />;
      case 'inventory':
        return <InventoryReportContent data={reportData} isMobile={isMobile} onTabChange={setActiveTab} />;
      case 'sales':
        return <SalesReportContent data={reportData} isMobile={isMobile} reportOptions={reportOptions} onTabChange={setActiveTab} />;
      case 'expenses':
        return <ExpensesReportContent data={reportData} isMobile={isMobile} onTabChange={setActiveTab} />;
      case 'financial':
        return <FinancialReportContent data={reportData} isMobile={isMobile} onTabChange={setActiveTab} />;
      case 'profit-loss':
        return <ProfitLossReportContent data={reportData} isMobile={isMobile} reportOptions={reportOptions} onTabChange={setActiveTab} />;
      default:
        return null;
    }
  };

  const ReportOptionsBar = () => {
    if (!selectedReport) return null;

    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]} align="middle">
          {/* Dashboard ไม่ต้องมีตัวเลือก */}
          {selectedReport === 'dashboard' && (
            <Col span={24}>
              <Space>
                <Button
                  type="primary"
                  onClick={fetchReport}
                  loading={loading}
                  icon={<ReloadOutlined />}
                  size="small"
                >
                  รีเฟรชข้อมูล
                </Button>
                <PDFButtons />
              </Space>
            </Col>
          )}

          {/* Inventory options */}
          {selectedReport === 'inventory' && (
            <>
              <Col xs={24} sm={8} md={6}>
                <Text strong>หมวดหมู่: </Text>
                {/* <Select
                  style={{ width: '100%' }}
                  size="small"
                  allowClear
                  placeholder="เลือกหมวดหมู่"
                >
                  <Option value="">ทั้งหมด</Option>
                  <Option value="food">อาหาร และเครื่องดื่ม</Option>
                  <Option value="home">เครื่องใช้</Option>
                </Select> */}
                <Select
                  value={reportOptions.category}
                  onChange={(value) => setReportOptions(prev => ({ ...prev, category: value }))}
                  style={{ width: '100%' }}
                  placeholder="เลือกหมวดหมู่"
                >
                  <Option value="">ทุกหมวดหมู่</Option>
                  {categories.map(category => (
                    <Option key={category.value} value={category.value}>
                      {category.label}
                    </Option>
                  ))}
                </Select>

              </Col>
              <Col xs={24} sm={8} md={6}>
                <Space>
                  <Text strong>สินค้าใกล้หมด:</Text>
                  <Switch
                    checked={reportOptions.lowStock}
                    onChange={(checked) => setReportOptions(prev => ({ ...prev, lowStock: checked }))}
                    size="small"
                  />
                </Space>
              </Col>
            </>
          )}

          {/* Date range for most reports */}
          {!['inventory'].includes(selectedReport) && (
            <Col xs={24} sm={8} md={6}>
              <Text strong>ช่วงวันที่: </Text>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="small"
              />
            </Col>
          )}

          {/* Group by for sales and expenses */}
          {(selectedReport === 'sales' || selectedReport === 'expenses') && (
            <Col xs={24} sm={8} md={6}>
              <Text strong>กลุ่มตาม: </Text>
              <Select
                value={reportOptions.groupBy}
                onChange={(value) => setReportOptions(prev => ({ ...prev, groupBy: value }))}
                style={{ width: '100%' }}
                size="small"
              >
                <Option value="day">รายวัน</Option>
                <Option value="month">รายเดือน</Option>
                {/* <Option value="year">รายปี</Option> */}
              </Select>
            </Col>
          )}

          {/* Period for profit-loss */}
          {selectedReport === 'profit-loss' && (
            <Col xs={24} sm={8} md={6}>
              <Text strong>รอบระยะเวลา: </Text>
              <Select
                value={reportOptions.period}
                onChange={(value) => setReportOptions(prev => ({ ...prev, period: value }))}
                style={{ width: '100%' }}
                size="small"
              >
                <Option value="day">รายวัน</Option>
                <Option value="month">รายเดือน</Option>
                <Option value="year">รายปี</Option>
              </Select>
            </Col>
          )}

          {/* Action buttons */}
          {selectedReport !== 'dashboard' && (
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button
                  type="primary"
                  onClick={fetchReport}
                  loading={loading}
                  icon={<ReloadOutlined />}
                  size="small"
                >
                  โหลดรายงาน
                </Button>
                <PDFButtons />
              </Space>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  // Auto load dashboard on mount
  useEffect(() => {
    if (selectedReport === 'dashboard') {
      fetchReport();
    }
  }, [selectedReport]);

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Title level={2}>ระบบรายงาน</Title>

      {!selectedReport ? (
        <Row gutter={[16, 16]}>
          {reports.map((report) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={report.key}>
              <Card
                hoverable
                style={{
                  textAlign: 'center',
                  minHeight: 180,
                  borderColor: report.color,
                  borderWidth: 2
                }}
                onClick={() => setSelectedReport(report.key)}
                bodyStyle={{ padding: 16 }}
              >
                <div style={{ fontSize: 36, marginBottom: 16, color: report.color }}>
                  {report.icon}
                </div>
                <Title level={4} style={{ marginBottom: 8 }}>
                  {report.title}
                </Title>
                <Text type="secondary">{report.description}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button
              onClick={() => {
                setSelectedReport(null);
                setReportData(null);
                setActiveTab('');
              }}
              style={{ marginRight: 16 }}
            >
              ← กลับ
            </Button>
            <Title level={3} style={{ display: 'inline' }}>
              {reports.find(r => r.key === selectedReport)?.title}
            </Title>
          </div>

          <ReportOptionsBar />

          {loading ? (
            <Card style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>กำลังโหลดรายงาน...</div>
            </Card>
          ) : reportData ? (
            <div id="report-content">
              {!['dashboard', 'financial', 'inventory'].includes(selectedReport) && <FinancialSummary data={financialData} />}
              {renderReportContent()}
            </div>
          ) : (
            <Card style={{ textAlign: 'center', padding: 48 }}>
              <Empty description="โปรดเลือกตัวเลือกและคลิก 'โหลดรายงาน' เพื่อแสดงข้อมูล" />
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// Dashboard Report Component
const DashboardReportContent = ({data,onTabChange}) => <Tabs defaultActiveKey="overview" onChange={onTabChange} items={[
  {key:'overview',label:'ภาพรวม',children:<FinancialSummary data={data}/>},
  {key:'activities',label:'เอกสารล่าสุด',children:<><Title level={5}>ใบเสร็จ/ใบแจ้งหนี้</Title><Table rowKey="id" dataSource={data.recent_activities?.receipts||[]} scroll={{x:600}} columns={[
    {title:'เลขที่',dataIndex:'invoice_number'},{title:'ลูกค้า',dataIndex:'customer_name'},{title:'ยอดรวม VAT',dataIndex:'total_amount',render:safeFormatCurrency}
  ]}/><Title level={5}>ค่าใช้จ่าย</Title><Table rowKey="id" dataSource={data.recent_activities?.expenses||[]} scroll={{x:600}} columns={[
    {title:'เลขที่',dataIndex:'expense_number'},{title:'รายละเอียด',dataIndex:'description'},{title:'ยอดรวม VAT',dataIndex:'total_amount',render:safeFormatCurrency}
  ]}/></>},
  {key:'events',label:'กำหนดการ',children:<Table rowKey="id" dataSource={data.upcoming_events||[]} columns={[{title:'กิจกรรม',dataIndex:'title'},{title:'วันที่',dataIndex:'start_date'}]}/>}
]}/>;

// Inventory report
const InventoryReportContent = ({ data, isMobile, onTabChange }) => {
  const { products = [], summary = {}, categoryBreakdown = [] } = data;

  const productColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 100,
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
      render: (value) => value || 'ไม่ระบุ',
    },
    {
      title: 'จำนวนคงเหลือ',
      dataIndex: 'stock',
      key: 'stock',
      align: 'right',
      render: (value, record) => (
        <span style={{ color: value <= record.min_stock ? '#ff4d4f' : undefined }}>
          {safeFormatNumber(value)} {record.unit || ''}
        </span>
      ),
    },
    {
      title: 'ต้นทุนต่อหน่วย',
      dataIndex: 'cost',
      key: 'cost',
      align: 'right',
      render: (value) => safeFormatCurrency(value),
    },
    {
      title: 'มูลค่าคงเหลือ',
      dataIndex: 'total_cost_value',
      key: 'total_cost_value',
      align: 'right',
      render: (value) => safeFormatCurrency(value),
    },
    {
      title: 'กำไรสะสม',
      dataIndex: 'potential_profit',
      key: 'potential_profit',
      align: 'right',
      render: (value) => (
        <span style={{ color: (value || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {safeFormatCurrency(value)}
        </span>
      ),
    },
  ];

  return (
    <Tabs defaultActiveKey="summary" onChange={onTabChange}>
      <TabPane tab="สรุปภาพรวม" key="summary">
        {summary && (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="จำนวนสินค้าทั้งหมด"
                  value={summary.total_products || 0}
                  suffix="รายการ"
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="จำนวนคงเหลือรวม"
                  value={summary.total_stock || 0}
                  suffix="ชิ้น"
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="มูลค่ารวมต้นทุน"
                  value={summary.total_cost_value || 0}
                  formatter={(value) => safeFormatCurrency(value)}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="มูลค่ารวมขาย"
                  value={summary.total_sell_value || 0}
                  formatter={(value) => safeFormatCurrency(value)}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="กำไรสะสม"
                  value={summary.potential_profit || 0}
                  formatter={(value) => safeFormatCurrency(value)}
                  valueStyle={{ color: (summary.potential_profit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                />
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="สินค้าใกล้หมด"
                  value={summary.low_stock_items || 0}
                  suffix="รายการ"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
            </Row>
          </>
        )}


        <Divider />

        {categoryBreakdown.length > 0 && (
          <>
            <Title level={4}>แยกตามหมวดหมู่</Title>
            <Row gutter={[16, 16]}>
              {categoryBreakdown.map((category, index) => (
                <Col xs={12} sm={8} md={6} key={index}>
                  <Card size="small">
                    <Statistic
                      title={category.category || 'ไม่ระบุ'}
                      value={category.total_cost_value || 0}
                      formatter={(value) => safeFormatCurrency(value)}
                      precision={0}
                    />
                    <Text type="secondary">
                      {safeFormatNumber(category.products)} รายการ
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
            <Divider />
          </>
        )}
      </TabPane>

      <TabPane tab="รายละเอียดสินค้า" key="products">
        <Table
          columns={productColumns}
          dataSource={products}
          rowKey="id"
          scroll={{ x: isMobile ? 1000 : undefined }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>
    </Tabs>
  );
};

// Component สำหรับรายงานยอดขาย (ปรับให้ตรงกับ API)
const SalesReportContent = ({ data, isMobile, reportOptions, onTabChange }) => {
  const { summary = {}, salesOverTime = [], topProducts = [], topCustomers = [] } = data;

  // กำหนด activeKey ตาม reportOptions.groupBy
  const getActiveKey = () => {
    switch (reportOptions.groupBy) {
      case 'day': return 'daily';
      case 'month': return 'monthly';
      case 'year': return 'yearly';
      default: return 'daily';
    }
  };

  const salesTimeColumns = [
    {
      title: 'ช่วงเวลา',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'จำนวนใบเสร็จ',
      dataIndex: 'total_receipts',
      key: 'total_receipts',
      align: 'right',
      render: (value) => safeFormatNumber(value, 'รายการ')
    },
    {
      title: 'ยอดขาย',
      dataIndex: 'total_sales',
      key: 'total_sales',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
    {
      title: 'ยอดขายเฉลี่ย',
      dataIndex: 'average_sale',
      key: 'average_sale',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
  ];

  return (
    <Tabs activeKey={getActiveKey()} onChange={onTabChange}>
      <TabPane tab="รายวัน" key="daily">
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="จำนวนใบเสร็จ"
              value={summary.total_receipts || 0}
              suffix="รายการ"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดขายรวม"
              value={summary.total_sales || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดขายเฉลี่ย"
              value={summary.average_sale || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ลูกค้าที่แตกต่าง"
              value={summary.unique_customers || 0}
              suffix="ราย"
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={salesTimeColumns}
          dataSource={salesOverTime.filter(item =>
            item.period && item.period.includes('-') && item.period.split('-').length === 3
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 600 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>

      <TabPane tab="รายเดือน" key="monthly">
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="จำนวนใบเสร็จ"
              value={summary.total_receipts || 0}
              suffix="รายการ"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดขายรวม"
              value={summary.total_sales || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดขายเฉลี่ย"
              value={summary.average_sale || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ลูกค้าที่แตกต่าง"
              value={summary.unique_customers || 0}
              suffix="ราย"
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={salesTimeColumns}
          dataSource={salesOverTime.filter(item =>
            item.period && (item.period.includes('เดือน') || (item.period.includes('-') && item.period.split('-').length === 2))
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 600 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>

      {/* <TabPane tab="รายปี" key="yearly">
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="จำนวนใบเสร็จ"
              value={summary.total_receipts || 0}
              suffix="รายการ"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดขายรวม"
              value={summary.total_sales || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดขายเฉลี่ย"
              value={summary.average_sale || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ลูกค้าที่แตกต่าง"
              value={summary.unique_customers || 0}
              suffix="ราย"
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={salesTimeColumns}
          dataSource={salesOverTime.filter(item =>
            item.period && (item.period.includes('-') || item.period.length === 4)
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 600 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane> */}
    </Tabs>
  );
};

// Component สำหรับรายงานค่าใช้จ่าย (ปรับให้ตรงกับ API)
const ExpensesReportContent = ({ data, isMobile, onTabChange }) => {
  const { summary = {}, expensesOverTime = [], expensesByCategory = [], expensesBySupplier = [] } = data;

  const timeColumns = [
    {
      title: 'ช่วงเวลา',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'จำนวนรายการ',
      dataIndex: 'total_expenses',
      key: 'total_expenses',
      align: 'right',
      render: (value) => safeFormatNumber(value, 'รายการ')
    },
    {
      title: 'ยอดรวม',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
  ];

  const categoryColumns = [
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'จำนวนรายการ',
      dataIndex: 'total_expenses',
      key: 'total_expenses',
      align: 'right',
      render: (value) => safeFormatNumber(value, 'รายการ')
    },
    {
      title: 'ยอดรวม',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
    {
      title: 'ค่าเฉลี่ย',
      dataIndex: 'average_expense',
      key: 'average_expense',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
  ];

  const supplierColumns = [
    {
      title: 'ผู้จำหน่าย',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (value) => value || 'ไม่ระบุ',
    },
    {
      title: 'จำนวนรายการ',
      dataIndex: 'total_expenses',
      key: 'total_expenses',
      align: 'right',
      render: (value) => safeFormatNumber(value, 'รายการ')
    },
    {
      title: 'ยอดรวม',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
    // {
    //   title: 'ค่าเฉลี่ย',
    //   dataIndex: 'average_expense',
    //   key: 'average_expense',
    //   align: 'right',
    //   render: (value) => safeFormatCurrency(value)
    // },
  ];

  return (
    <Tabs defaultActiveKey="summary" onChange={onTabChange}>
      <TabPane tab="สรุปค่าใช้จ่าย" key="summary">
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="จำนวนรายการทั้งหมด"
              value={summary.total_expenses || 0}
              suffix="รายการ"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ยอดรวมค่าใช้จ่าย"
              value={summary.total_amount || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="ค่าใช้จ่ายเฉลี่ย"
              value={summary.average_expense || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col>
          {/* <Col xs={12} sm={6}>
            <Statistic
              title="ค่าใช้จ่ายสูงสุด"
              value={summary.max_expense || 0}
              formatter={(value) => safeFormatCurrency(value)}
            />
          </Col> */}
        </Row>
      </TabPane>

      <TabPane tab="ตามช่วงเวลา" key="time">
        <Table
          columns={timeColumns}
          dataSource={expensesOverTime}
          rowKey="period"
          scroll={{ x: isMobile ? 600 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>

      <TabPane tab="ตามหมวดหมู่" key="category">
        <Table
          columns={categoryColumns}
          dataSource={expensesByCategory}
          rowKey="category"
          scroll={{ x: isMobile ? 800 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>

      <TabPane tab="ตามผู้จำหน่าย" key="supplier">
        <Table
          columns={supplierColumns}
          dataSource={expensesBySupplier}
          rowKey={(record, index) => index}
          scroll={{ x: isMobile ? 800 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>
    </Tabs>
  );
};

// Component สำหรับรายงานงบการเงิน (ปรับให้ตรงกับ API)
const FinancialReportContent = ({ data, isMobile, onTabChange }) => <Tabs defaultActiveKey="summary" onChange={onTabChange}
  items={[
    {key:'summary',label:'สรุปทางการเงิน',children:<><FinancialSummary data={data} /><Text type="secondary">กำไรสุทธิจากรายการลงบัญชีแล้ว · ยอดเอกสารรวม VAT แสดงแยกจากกำไรบัญชี</Text></>},
    {key:'cashflow',label:'รับ–จ่ายตามเอกสาร',children:<Table rowKey="date" dataSource={data.cashFlowData||[]} scroll={{x:600}} columns={[
      {title:'วันที่',dataIndex:'date'},{title:'รายรับรวม VAT',dataIndex:'revenue',render:safeFormatCurrency},
      {title:'รายจ่ายรวม VAT',dataIndex:'expenses',render:safeFormatCurrency},{title:'ส่วนต่างรวม VAT',dataIndex:'difference',render:safeFormatCurrency}
    ]}/>}
  ]}/>;

// Component สำหรับรายงานกำไร/ขาดทุน (ปรับให้ตรงกับ API)
const ProfitLossReportContent = ({ data, isMobile, reportOptions, onTabChange }) => {

  console.log('ข้อมูล', data);

  // กำหนด activeKey ตาม reportOptions.period
  const getActiveKey = () => {
    switch (reportOptions.period) {
      case 'day': return 'daily';
      case 'week': return 'weekly';
      case 'month': return 'monthly';
      case 'year': return 'yearly';
      default: return 'daily';
    }
  };

  const profitLossColumns = [
    {
      title: 'ช่วงเวลา',
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: 'รายได้',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
    {
      title: 'ค่าใช้จ่าย',
      dataIndex: 'total_expenses',
      key: 'total_expenses',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
    {
      title: 'ต้นทุนขาย',
      dataIndex: 'cost_of_goods_sold',
      key: 'cost_of_goods_sold',
      align: 'right',
      render: (value) => safeFormatCurrency(value)
    },
    {
      title: 'กำไร/ขาดทุน',
      dataIndex: 'net_profit',
      key: 'net_profit',
      align: 'right',
      render: (value) => (
        <span style={{ color: (value || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {safeFormatCurrency(value)}
        </span>
      ),
    },
  ];

  // คำนวณสรุปข้อมูล
  const ledgerTotals = data.financial_summary?.ledger || {};
  const totals = {totalRevenue:ledgerTotals.revenue || 0,totalExpenses:ledgerTotals.operating_expenses || 0,netProfit:ledgerTotals.net_profit || 0};

  console.log('total', totals)

  return (
    <Tabs activeKey={getActiveKey()} onChange={onTabChange}>
      <TabPane tab="รายวัน" key="daily">
        <Row gutter={[16, 16]}>
          <Col xs={8}>
            <Statistic
              title="รายได้รวม"
              value={totals.totalRevenue}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="ค่าใช้จ่ายรวม"
              value={totals.totalExpenses}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="กำไร/ขาดทุนสุทธิ"
              value={totals.netProfit}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: totals.netProfit >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={profitLossColumns}
          dataSource={data.filter(item =>
            item.period && item.period.includes('-') && item.period.split('-').length === 3
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 800 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>

      {/* <TabPane tab="รายสัปดาห์" key="weekly">
        <Row gutter={[16, 16]}>
          <Col xs={8}>
            <Statistic
              title="รายได้รวม"
              value={totals.total_Revenue}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="ค่าใช้จ่ายรวม"
              value={totals.totalExpenses}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="กำไร/ขาดทุนสุทธิ"
              value={totals.netProfit}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: totals.netProfit >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={profitLossColumns}
          dataSource={data.filter(item =>
            item.period && (item.period.includes('-'))
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 800 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane> */}

      <TabPane tab="รายเดือน" key="monthly">
        <Row gutter={[16, 16]}>
          <Col xs={8}>
            <Statistic
              title="รายได้รวม"
              value={totals.totalRevenue}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="ค่าใช้จ่ายรวม"
              value={totals.totalExpenses}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="กำไร/ขาดทุนสุทธิ"
              value={totals.netProfit}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: totals.netProfit >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={profitLossColumns}
          dataSource={data.filter(item =>
            item.period && (item.period.includes('เดือน') || (item.period.includes('-') && item.period.split('-').length === 2))
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 800 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>

      <TabPane tab="รายปี" key="yearly">
        <Row gutter={[16, 16]}>
          <Col xs={8}>
            <Statistic
              title="รายได้รวม"
              value={totals.totalRevenue}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="ค่าใช้จ่ายรวม"
              value={totals.totalExpenses}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="กำไร/ขาดทุนสุทธิ"
              value={totals.netProfit}
              formatter={(value) => safeFormatCurrency(value)}
              valueStyle={{ color: totals.netProfit >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
          </Col>
        </Row>

        <Divider />

        <Table
          columns={profitLossColumns}
          dataSource={data.filter(item =>
            item.period && (item.period.includes('ปี') || item.period.length === 4)
          )}
          rowKey="period"
          scroll={{ x: isMobile ? 800 : undefined }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </TabPane>
    </Tabs>
  );
};

export default ReportsPage;
